import { type Address } from 'viem'
import axios from 'axios'
import { BACKEND_BASE_URI } from '@/config/constants'
import type { ResponseBadge } from '@/types/super-chain'
import local from '@/services/local-storage/local'
import type { Setter } from '@/services/local-storage/useLocalStorage'
import { getSiweToken } from '@/utils/helpers'
import { BadgeWithPrize } from '@/types/badges'

export type Perks = {
  name: string
  value: number
}[]
class BadgesService {
  httpInstance = axios.create({
    baseURL: BACKEND_BASE_URI,
    //withCredentials: true,
  })
  public switchFavoriteBadge(
    badgeId: number,
    safe: Address,
    isFavorite: boolean,
    setLocalStorage: Setter<string>,
  ): void {
    if (!local) return
    const favoriteBadges = local.getItem<string>('favoriteBadges')
    if (!favoriteBadges) {
      setLocalStorage(JSON.stringify([badgeId.toString().concat(safe)]))
    } else {
      const parsedFavoriteBadges = JSON.parse(favoriteBadges)
      if (isFavorite) {
        parsedFavoriteBadges.push(badgeId.toString().concat(safe))
      } else {
        const index = parsedFavoriteBadges.findIndex((id: string) => id === badgeId.toString().concat(safe))
        parsedFavoriteBadges.splice(index, 1)
      }
      setLocalStorage(JSON.stringify(parsedFavoriteBadges))
    }
  }
  public getFavoriteBadges(safe: Address): string[] {
    if (!local) return []
    const favoriteBadges = local.getItem<string>('favoriteBadges')
    if (!favoriteBadges) return []

    return JSON.parse(favoriteBadges)
      .filter((id: string) => id.endsWith(safe))
      .map((id: string) => id.replace(safe, ''))
  }
  public async getBadgesWithPrizes(account?: Address): Promise<{
    currentBadges: BadgeWithPrize[]
  }> {
    const response = await this.httpInstance.get<{
      currentBadges: BadgeWithPrize[]
    }>(`/user/${account}/badges`)

    response.data.currentBadges = response.data.currentBadges.sort((a, b) => {
      const aIsLastTier = a.tier === a.badgeTiers.length
      const bIsLastTier = b.tier === b.badgeTiers.length

      if (aIsLastTier === bIsLastTier) return 0
      return aIsLastTier ? 1 : -1
    })

    response.data.currentBadges.forEach((badge) => {
      badge.metadata.name = badge.metadata.name.replaceAll('FarCaster', 'Farcaster')
      badge.metadata.description = badge.metadata.description.replaceAll('FarCaster', 'Farcaster')
      badge.metadata.name = badge.metadata.name.replaceAll('Aidrop', 'Airdrop')
      if (badge.metadata.name == 'Self verification') {
        badge.metadata.condition = 'Verified with Self Protocol'
      }
    })
    return response.data
  }
  public async getBadges(account?: Address): Promise<{
    currentBadges: ResponseBadge[]
  }> {
    const response = await this.httpInstance.get<{
      currentBadges: ResponseBadge[]
    }>(`/user/${account}/badges`)
    return response.data
  }
  // public async attestBadges(account: Address, captchaToken: string | null) {
  //   const response = await this.httpInstance.post(`/user/${account}/badges/claim`, { captchaToken })
  //   return response.data
  // }

  public async attestBadges(account: Address, extraData: any): Promise<any> {
    const maxRetries: number = 1
    let attempt: number = 0
    const token = getSiweToken()
    while (attempt <= maxRetries) {
      try {
        const response = await this.httpInstance.post(`/user/${account}/badges/claim`, extraData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.status === 201) {
          return response.data
        }

        attempt++
      } catch (error) {
        attempt++

        if (attempt > maxRetries) {
          throw error
        }
      }
    }

    throw new Error('Claiming failed after retries')
  }

  public async getPerks(account: Address) {
    const response = await this.httpInstance.get<{ perks: Perks }>(`/user/${account}/perks`)
    return response.data.perks
  }
  public async getPerksByLevel(level: number) {
    const response = await this.httpInstance.get<{ perks: Perks }>(`/perks/${level}`)
    return response.data.perks
  }
}
const badgesService = new BadgesService()
export default badgesService
